import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Icon,
  Input,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaSync, FaPrint, FaEdit } from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";

const ListHouse = () => {
  const [houses, setHouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [currentHouse, setCurrentHouse] = useState(null);
  const [editedCaption, setEditedCaption] = useState("");
  const toast = useToast();

  const colors = [
    "pink.100",
    "purple.100",
    "blue.100",
    "green.100",
    "yellow.100",
  ];

  // Fetch house data from the API
  useEffect(() => {
    const fetchHouses = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/admin/house/list_all_house.php`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setHouses(data.events);
      } catch (error) {
        toast({
          title: "Error fetching houses",
          description: "Couldn't fetch house data.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchHouses();
  }, [toast]);

  const handleEdit = (house) => {
    setCurrentHouse(house);
    setEditedCaption(house.caption);
    setEditModalOpen(true);
  };

  const handleDelete = async (houseId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this house?"
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${BASE_URL}/admin/house/delete_house.php`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ house_id: houseId }),
        }
      );

      const responseData = await response.json();
      console.log("Delete response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || "Network response was not ok");
      }

      setHouses((prevHouses) =>
        prevHouses.filter((house) => house.id !== houseId)
      );

      toast({
        title: "House deleted",
        // description: `House with ID ${houseId} has been deleted.`,
        description: `House  has been deleted successfully.`,

        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error deleting house",
        description: error.message || "Couldn't delete house data.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEditSubmit = async () => {
    if (!currentHouse) return;

    try {
      const response = await fetch(
        `${BASE_URL}/admin/house/edit_house.php`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            house_id: currentHouse.id,
            caption: editedCaption,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      setHouses((prevHouses) =>
        prevHouses.map((house) =>
          house.id === currentHouse.id
            ? { ...house, caption: editedCaption }
            : house
        )
      );

      toast({
        title: "House updated",
        description: `House with ID ${currentHouse.id} has been updated.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setEditModalOpen(false);
      setCurrentHouse(null);
      setEditedCaption("");
    } catch (error) {
      toast({
        title: "Error updating house",
        description: "Couldn't update house data.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6}>
      <Box maxW="1400px" mx="auto" borderRadius="lg" p={6}>
        {isLoading ? (
          <Text textAlign="center" color="gray.500">
            Loading...
          </Text>
        ) : (
          <SimpleGrid columns={[1, 2, 3, 4]} spacing={4}>
            {houses?.map((house, index) => (
              <Box
                key={house.id}
                borderWidth={1}
                borderRadius="md"
                p={4}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                bg={colors[index % colors.length]}
                // borderColor="gray.200"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "sm",
                  borderColor: "gray.300",
                }}
                transition="all 0.2s"
              >
                <Text fontWeight="medium" mr={2} color="gray.700" fontSize="md">
                  {house.caption}
                </Text>
                <Box>
                  <IconButton
                    aria-label="Edit house"
                    icon={<EditIcon />}
                    colorScheme="purple"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(house)}
                    mr={2}
                  />
                  <IconButton
                    aria-label="Delete house"
                    icon={<DeleteIcon />}
                    colorScheme="pink"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(house.id)}
                  />
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}

        <Modal
          isOpen={isEditModalOpen}
          size="lg"
          motionPreset="slideInBottom"
          onClose={() => setEditModalOpen(false)}
        >
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent
            borderRadius="lg"
            boxShadow="xl"
            bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
            color="gray.700"
            p={6}
            maxW="700px"
            maxH="550px"
            overflowY="auto"
          >
            <ModalHeader
              fontSize="xl"
              fontWeight="bold"
              textAlign="center"
              color="teal.600"
            >
              <Icon as={FaEdit} boxSize={5} mr={2} />
              Edit House
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody
              overflowY="auto"
              px={4}
              css={{
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "10px",
                  backdropFilter: "blur(8px)",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(173, 216, 230, 0.6)",
                  borderRadius: "10px",
                  backdropFilter: "blur(8px)",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "rgba(173, 216, 230, 0.9)",
                },
              }}
            >
              
              <Input
              
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="House Caption"
                value={editedCaption}
                onChange={(e) => setEditedCaption(e.target.value)}
                focusBorderColor="purple.400"
              />
            </ModalBody>
            <ModalFooter>
              <Button
                bgGradient="linear(to-r, teal.400, teal.600)"
                color="white"
                _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
                onClick={handleEditSubmit}
              >
                Save
              </Button>
              {/* <Button variant="ghost" onClick={() => setEditModalOpen(false)} ml={3}>
                Cancel
              </Button> */}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default ListHouse;
